import { ltmsConversationDefaultProps } from 'src/app/features/projects/settings/project-interaction-settings-home/constants/default';

const LANGUAGE_ISO: Record<string, string> = {
  abkhaz: 'AB', afar: 'AA', afrikaans: 'AF', akan: 'AK', albanian: 'SQ', amharic: 'AM',
  arabic: 'AR', aragonese: 'AN', armenian: 'HY', assamese: 'AS', avaric: 'AV', avestan: 'AE',
  aymara: 'AY', azerbaijani: 'AZ', bambara: 'BM', bashkir: 'BA', basque: 'EU', belarusian: 'BE',
  bengali: 'BN', bihari: 'BH', bislama: 'BI', bosnian: 'BS', breton: 'BR', bulgarian: 'BG',
  burmese: 'MY', catalan_valencian: 'CA', chamorro: 'CH', chechen: 'CE', chichewa_chewa_nyanja: 'NY',
  chinese: 'ZH', chuvash: 'CV', cornish: 'KW', corsican: 'CO', cree: 'CR', croatian: 'HR',
  czech: 'CS', danish: 'DA', divehi_dhivehi_maldivian_: 'DV', dutch: 'NL', english: 'EN',
  esperanto: 'EO', estonian: 'ET', ewe: 'EE', faroese: 'FO', fijian: 'FJ', finnish: 'FI',
  french: 'FR', fula_fulah_pulaar_pular: 'FF', galician: 'GL', georgian: 'KA', german: 'DE',
  greek_modern: 'EL', guarani: 'GN', gujarati: 'GU', haitian_haitian_creole: 'HT', hausa: 'HA',
  hebrew_modern: 'HE', herero: 'HZ', hindi: 'HI', hinglish: 'HI', chhattisgarhi: 'CG', hiri_motu: 'HO', hungarian: 'HU',
  interlingua: 'IA', indonesian: 'ID', irish: 'GA', igbo: 'IG', inupiaq: 'IK', ido: 'IO',
  icelandic: 'IS', italian: 'IT', inuktitut: 'IU', japanese: 'JA', javanese: 'JV', kannada: 'KN',
  kanuri: 'KR', kashmiri: 'KS', kazakh: 'KK', khmer: 'KM', kikuyu_gikuyu: 'KI', kinyarwanda: 'RW',
  kirghiz_kyrgyz: 'KY', komi: 'KV', kongo: 'KG', korean: 'KO', kurdish: 'KU', kwanyama_kuanyama: 'KJ',
  latin: 'LA', luxembourgish_letzeburgesch: 'LB', luganda: 'LG', limburgish_limburgan_limburger: 'LI',
  lingala: 'LN', lao: 'LO', lithuanian: 'LT', luba_katanga: 'LU', latvian: 'LV', manx: 'GV',
  macedonian: 'MK', malagasy: 'MG', malay: 'MS', malayalam: 'ML', maltese: 'MT', maori: 'MI',
  marathi_mar_h_: 'MR', marathi: 'MR', marshallese: 'MH', mongolian: 'MN', nauru: 'NA',
  navajo_navaho: 'NV', norwegian_bokmal: 'NB', north_ndebele: 'ND', nepali: 'NE', ndonga: 'NG',
  norwegian_nynorsk: 'NN', norwegian: 'NO', nuosu: 'II', south_ndebele: 'NR', occitan: 'OC',
  ojibwe_ojibwa: 'OJ', oromo: 'OM', oriya: 'OR', ossetian_ossetic: 'OS', panjabi_punjabi: 'PA',
  punjabi: 'PA', pali: 'PI', persian: 'FA', polish: 'PL', pushto: 'PS', portuguese: 'PT',
  quechua: 'QU', romansh: 'RM', kirundi: 'RN', romanian_moldavian_moldovan: 'RO', russian: 'RU',
  sanskrit: 'SA', sardinian: 'SC', sindhi: 'SD', northern_sami: 'SE', samoan: 'SM', sango: 'SG',
  serbian: 'SR', scottish: 'GD', shona: 'SN', sinhala_sinhalese: 'SI', slovak: 'SK', slovene: 'SL',
  somali: 'SO', southern_sotho: 'ST', spanish_castilian: 'ES', spanish: 'ES', sundanese: 'SU',
  swahili: 'SW', swati: 'SS', swedish: 'SV', tamil: 'TA', telugu: 'TE', tajik: 'TG', thai: 'TH',
  tigrinya: 'TI', tibetan: 'BO', turkmen: 'TK', tagalog: 'TL', tswana: 'TN', tonga: 'TO',
  turkish: 'TR', tsonga: 'TS', tatar: 'TT', twi: 'TW', tahitian: 'TY', uighur: 'UG',
  ukrainian: 'UK', urdu: 'UR', uzbek: 'UZ', venda: 'VE', vietnamese: 'VI', volapuk: 'VO',
  walloon: 'WA', welsh: 'CY', wolof: 'WO', western_frisian: 'FY', xhosa: 'XH', yiddish: 'YI',
  yoruba: 'YO', zhuang: 'ZA',
};

export function languageShortCode(key: string, name = ''): string {
  if (LANGUAGE_ISO[key]) {
    return LANGUAGE_ISO[key];
  }
  const letters = String(name || key).replace(/[^A-Za-z]/g, '');
  return (letters.slice(0, 2) || '??').toUpperCase();
}

const CHANNEL_LANGUAGE_KEYS = [
  'english',
  'hindi',
  'hinglish',
  'chhattisgarhi',
  'bengali',
  'telugu',
  'marathi',
  'tamil',
  'urdu',
  'gujarati',
  'kannada',
  'oriya',
  'malayalam',
  'punjabi',
  'assamese',
  'kashmiri',
  'nepali',
  'sindhi',
  'sanskrit',
  'arabic',
  'spanish_castilian',
  'french',
  'chinese',
  'portuguese',
  'russian',
  'german',
  'japanese',
  'korean',
  'indonesian',
  'thai',
  'turkish',
  'italian',
  'abkhaz',
  'afar',
  'afrikaans',
  'akan',
  'albanian',
  'amharic',
  'aragonese',
  'armenian',
  'avaric',
  'avestan',
  'aymara',
  'azerbaijani',
  'bambara',
  'bashkir',
  'basque',
  'belarusian',
  'bihari',
  'bislama',
  'bosnian',
  'breton',
  'bulgarian',
  'burmese',
  'catalan_valencian',
  'chamorro',
  'chechen',
  'chichewa_chewa_nyanja',
  'chuvash',
  'cornish',
  'corsican',
  'cree',
  'croatian',
  'czech',
  'danish',
  'divehi_dhivehi_maldivian_',
  'dutch',
  'esperanto',
  'estonian',
  'ewe',
  'faroese',
  'fijian',
  'finnish',
  'fula_fulah_pulaar_pular',
  'galician',
  'georgian',
  'greek_modern',
  'guarani',
  'haitian_haitian_creole',
  'hausa',
  'hebrew_modern',
  'herero',
  'hiri_motu',
  'hungarian',
  'interlingua',
  'irish',
  'igbo',
  'inupiaq',
  'ido',
  'icelandic',
  'inuktitut',
  'javanese',
  'kanuri',
  'kazakh',
  'khmer',
  'kikuyu_gikuyu',
  'kinyarwanda',
  'kirghiz_kyrgyz',
  'komi',
  'kongo',
  'kurdish',
  'kwanyama_kuanyama',
  'latin',
  'luxembourgish_letzeburgesch',
  'luganda',
  'limburgish_limburgan_limburger',
  'lingala',
  'lao',
  'lithuanian',
  'luba_katanga',
  'latvian',
  'manx',
  'macedonian',
  'malagasy',
  'malay',
  'maltese',
  'maori',
  'marathi_mar_h_',
  'marshallese',
  'mongolian',
  'nauru',
  'navajo_navaho',
  'norwegian_bokmal',
  'north_ndebele',
  'ndonga',
  'norwegian_nynorsk',
  'norwegian',
  'nuosu',
  'south_ndebele',
  'occitan',
  'ojibwe_ojibwa',
  'oromo',
  'ossetian_ossetic',
  'panjabi_punjabi',
  'pali',
  'persian',
  'polish',
  'pushto',
  'quechua',
  'romansh',
  'kirundi',
  'romanian_moldavian_moldovan',
  'sardinian',
  'northern_sami',
  'samoan',
  'sango',
  'serbian',
  'scottish',
  'shona',
  'sinhala_sinhalese',
  'slovak',
  'slovene',
  'somali',
  'southern_sotho',
  'sundanese',
  'swahili',
  'swati',
  'swedish',
  'tajik',
  'tigrinya',
  'tibetan',
  'turkmen',
  'tagalog',
  'tswana',
  'tonga',
  'tsonga',
  'tatar',
  'twi',
  'tahitian',
  'uighur',
  'ukrainian',
  'uzbek',
  'venda',
  'vietnamese',
  'volapuk',
  'walloon',
  'welsh',
  'wolof',
  'western_frisian',
  'xhosa',
  'yiddish',
  'yoruba',
  'zhuang',
];

const CHANNEL_LANGUAGE_EXTRAS: Array<{ key: string; name: string; native_name: string }> = [
  { key: 'chhattisgarhi', name: 'Chhattisgarhi', native_name: 'छत्तीसगढ़ी' },
];

export const LANGUAGE_OPTIONS: Array<{ key: string; name: string; native_name: string; short: string }> = (() => {
  const catalog = new Map(
    (ltmsConversationDefaultProps.languages as Array<{ key: string; name: string; native_name?: string }>)
      .map((lang) => [String(lang.key), lang]),
  );
  CHANNEL_LANGUAGE_EXTRAS.forEach((lang) => catalog.set(lang.key, lang));
  return CHANNEL_LANGUAGE_KEYS
    .map((key) => catalog.get(key))
    .filter((lang): lang is { key: string; name: string; native_name?: string } => Boolean(lang))
    .map((lang) => ({
    key: String(lang.key),
    name: String(lang.name),
    native_name: lang.native_name ? String(lang.native_name) : '',
    short: languageShortCode(String(lang.key), String(lang.name)),
  }));
})();
